/**
 * How a call window opens: the account's default, and this browser's answer to it.
 *
 * Two layers, and the split is the whole point. The SERVER holds what a desk
 * that has never been asked should see - set once in Settings -> Integrations,
 * and it follows an operator onto a new laptop. The BROWSER holds what this
 * particular operator last chose with the buttons in the call window itself,
 * and it wins, because a control that quietly reverts is a broken control.
 *
 * The fetch is cached for the life of the tab. Every operator's browser reads
 * this at start-up and the answer is two booleans that change about once a year,
 * so re-asking on each call would be a request per call for nothing. Settings
 * calls `forgetCallWindowPrefs()` after a save, which is the one moment the
 * answer can have changed under us.
 */
import { api } from "./api";

export interface CallWindowPrefs {
  /** Open filling the screen, so the whole AI panel is readable at once. */
  fullscreen: boolean;
  /** Open with the AI panel already out beside the call. */
  pilotOpen: boolean;
}

/**
 * What to draw when the server cannot be reached.
 *
 * The same values `defaults()` ships on the API side, and they have to stay in
 * step: this is what an operator sees if the prefs request fails while a call is
 * already ringing, and "the network blipped" is not a reason to hand them a
 * different window than the one they had yesterday.
 */
export const CALL_WINDOW_FALLBACK: CallWindowPrefs = { fullscreen: true, pilotOpen: true };

let pending: Promise<CallWindowPrefs> | null = null;

export function getCallWindowPrefs(): Promise<CallWindowPrefs> {
  pending ??= api
    .get<CallWindowPrefs>("/admin/crm/calls/window-prefs")
    .then((prefs) => ({
      fullscreen: prefs.fullscreen !== false,
      pilotOpen: prefs.pilotOpen !== false,
    }))
    .catch(() => {
      // Cleared rather than cached: a request that failed because the session
      // was still being established should be retried by the next call window,
      // not remembered as the answer for the rest of the day.
      pending = null;
      return CALL_WINDOW_FALLBACK;
    });
  return pending;
}

/** The stored default just changed. The next window asks again. */
export function forgetCallWindowPrefs(): void {
  pending = null;
}

/**
 * This browser's own answer, or `null` for "never chosen - use the default".
 *
 * `null` and `false` are genuinely different states here and the storage layer
 * is the only place that can tell them apart: an operator who has never touched
 * the fullscreen button must follow the account default when it changes, and one
 * who pressed it must not be overruled by it.
 */
export function readLocalFlag(key: string): boolean | null {
  try {
    const raw = localStorage.getItem(key);
    return raw === "1" ? true : raw === "0" ? false : null;
  } catch {
    // Private mode, or storage switched off. Nothing was ever chosen here.
    return null;
  }
}

export function writeLocalFlag(key: string, value: boolean): void {
  try {
    localStorage.setItem(key, value ? "1" : "0");
  } catch {
    // Not being able to remember the choice is no reason to refuse it.
  }
}
